<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { capitalize, get } from 'lodash'
import { onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { safeFormat } from '@/modules/shared/utils/date'
import {
  ActionItem,
  ActionsGroup,
  ActionsMenu,
  RouterLinkBack,
  VAvatar,
  VButton,
  VButtonInvisible,
  VIcon,
  VMailTo,
  VModal,
  VSection,
  VTable,
  VTextField,
} from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { useModal } from '@/modules/shared/composables/use-modal'
import { unflattenKey } from '@/modules/shared/utils/v-table'
import { rails_url } from '@/modules/shared/utils/rails'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import IndividualsNav from '../components/individuals-nav.vue'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { useIndividualStore } from '../stores/better-individual-store'

const { t } = useExtendedI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const investingInvestorStore = useInvestingInvestorStore()
const individualStore = useIndividualStore()

const skeleton = ref(true)

const deleteAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.removeIndividual(item.id)
  await investingInvestorStore.fetchIndividuals('all')
}

const revokeAccessAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.revokeAccessIndividual(item.id, 'all')
}

const activateAccessAction = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await investingInvestorStore.activateAccessIndividual(item.id, 'all')
}

const sendRegistrationInvitation = async (item) => {
  await individualStore.resendInvitation(item.id, { slug: route.params.slug as string })
  alert(`${item.name} has been sent a link to register`)
}

const getStatus = (item) => {
  const statuses = unflattenKey(item, 'status')
  return statuses.find((status) => status.group_id === workspaceStore.current.id)?.status
}

///////////////////////////////////////////////////////////////////////////////
// Invite Admin
///////////////////////////////////////////////////////////////////////////////
const adminFormModal = useModal()

const admin_initial_state = {
  first_name: null,
  last_name: null,
  email: null,
}

const admin_rules = {
  first_name: { required },
  last_name: { required },
  email: { required, email },
}
const admin_form = ref({ ...admin_initial_state })
const v$ = useVuelidate(admin_rules, admin_form, { $lazy: true })

const inviteAdmin = () => {
  v$.value.$reset()
  admin_form.value = { ...admin_initial_state }
  adminFormModal.open()
}

const saveAdmin = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  await individualStore.addGroupAdmin(admin_form.value, {
    slug: route.params.slug as string,
  })
  adminFormModal.close()
  await investingInvestorStore.fetchIndividuals('all')
}

///////////////////////////////////////////////////////////////////////////////
// Table
///////////////////////////////////////////////////////////////////////////////
const getAdminRole = (individual) => {
  const roles = unflattenKey(individual, 'roles') || []
  return roles.find((r) => r.group === workspaceStore.current.name && r.role === 'Group Admin')
}

const admins = computed(() => {
  return investingInvestorStore.individuals.filter((individual) => getAdminRole(individual))
})

const columns = [
  {
    key: 'name',
    name: capitalize(t('shared.name', 0)),
    type: 'string',
    align: 'left',
    is_visible: true,
    fixed: true,
  },
  {
    key: 'email',
    name: capitalize(t('shared.email')),
    type: 'string',
    align: 'left',
    is_visible: true,
  },
  {
    key: 'account_status',
    name: 'Account Status',
    type: 'string',
    align: 'left',
    is_visible: true,
  },
  {
    key: 'last_login',
    name: capitalize(t('shared.last login at')),
    type: 'date',
    align: 'left',
    is_visible: true,
  },
  {
    key: 'actions',
    name: '',
    type: 'actions',
    align: 'right',
    is_visible: true,
  },
]

onMounted(async () => {
  await workspaceStore.fetchCurrent()
  await investingInvestorStore.fetchIndividuals('all')
  skeleton.value = false
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6 flex items-center justify-between">
      <div>
        <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
          <VButtonInvisible>← {{ capitalize(t('shared.back')) }}</VButtonInvisible>
        </RouterLinkBack>
      </div>
    </div>

    <VSection>
      <IndividualsNav />
      <div class="mb-3 flex items-center justify-end">
        <div v-if="authStore.is_site_or_group_admin" class="flex items-center space-x-1.5">
          <VButton :click="inviteAdmin" size="md" variant="v-blue">
            <div class="mr-1 flex items-center space-x-2">
              <div><VIcon name="Plus" /></div>
              <div>Invite Admin</div>
            </div>
          </VButton>
        </div>
      </div>

      <VTable
        :name="`admins-${route.params.slug}`"
        :columns="columns"
        :items="admins"
        :skeleton="skeleton"
        :slots="['name', 'email', 'account_status', 'last_login', 'actions']"
        no_data_message="No admins found. Use 'Invite Admin' to add a new group administrator."
      >
        <template #name="{ item }">
          <RouterLink
            class="flex cursor-pointer items-center space-x-3"
            :to="{
              name: 'investing.individual-overview',
              params: { individual_id: item.id },
            }"
          >
            <VAvatar :image="item.profile_picture" :initials="item.initials" size="sm" />
            <div class="hyperlink -mt-0.5">
              {{ item.name }}
            </div>
          </RouterLink>
        </template>
        <template #email="{ item }">
          <VMailTo :email="item.email" />
        </template>
        <template #account_status="{ item }">
          <span
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="item.last_login ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
          >
            {{ item.last_login ? 'Active' : 'Invited' }}
          </span>
        </template>
        <template #last_login="{ item }">
          {{ safeFormat(item.last_login, "EEEE, MMMM d, y 'at' h:mma", 'Never') }}
        </template>
        <template #actions="{ item }">
          <div class="flex items-center justify-end space-x-1.5">
            <ActionsMenu v-if="authStore.is_site_or_group_admin">
              <ActionsGroup>
                <ActionItem
                  @click="
                    () => router.push({ name: 'investing.individual.account', params: { individual_id: item.id } })
                  "
                  icon="LinkExternal"
                  :text="capitalize(t('shared.edit profile'))"
                  tag="a"
                />
                <ActionItem
                  v-if="
                    authStore.is_site_or_group_admin && (getStatus(item) == 'Active' || getStatus(item) == 'Pending')
                  "
                  icon="FileX"
                  :text="capitalize(t('shared.revoke access'))"
                  @click="revokeAccessAction(item)"
                />
                <ActionItem
                  v-if="getStatus(item) !== 'Active' && authStore.is_site_or_group_admin"
                  icon="ThumbsUp"
                  :text="capitalize(t('shared.activate'))"
                  @click="activateAccessAction(item)"
                />
                <ActionItem
                  v-if="authStore.is_site_admin"
                  icon="Glasses02"
                  :text="capitalize(t('shared.view as'))"
                  @click="authStore.viewAs(get(item, 'user.id'))"
                />
                <ActionItem
                  v-if="authStore.is_site_admin"
                  icon="LinkExternal"
                  tag="a"
                  :text="capitalize(t('shared.merge'))"
                  :href="`${rails_url()}/investors/${get(item, 'id')}/merge`"
                />
                <ActionItem
                  icon="FileCheck"
                  tag="a"
                  :text="capitalize(t('shared.resend invitation email'))"
                  :disabled="!authStore.is_site_or_group_admin || !!item.last_login"
                  @click="sendRegistrationInvitation(item)"
                />
              </ActionsGroup>
              <ActionsGroup>
                <ActionItem
                  v-if="authStore.is_site_or_group_admin"
                  icon="Trash"
                  :text="capitalize(t('shared.delete'))"
                  @click="() => deleteAction(item)"
                />
              </ActionsGroup>
            </ActionsMenu>
          </div>
        </template>
      </VTable>
    </VSection>

    <VModal :modalStore="adminFormModal">
      <template #main>
        <VSection label="Invite Admin">
          <div class="space-y-3">
            <VTextField
              v-model="admin_form.first_name"
              :label="capitalize(t('shared.first name'))"
              property="first_name"
              :v$="v$"
            />
            <VTextField
              v-model="admin_form.last_name"
              :label="capitalize(t('shared.last name'))"
              property="last_name"
              :v$="v$"
            />
            <VTextField
              v-model="admin_form.email"
              :label="capitalize(t('shared.email'))"
              property="email"
              type="email"
              :v$="v$"
            />
          </div>
        </VSection>
      </template>
      <template #footer>
        <VButton :click="saveAdmin" variant="primary" size="lg" class="w-full">Invite Admin</VButton>
      </template>
    </VModal>
  </TheLayout>
</template>
